/**
 * i18n autosuficiente para payments-ui (Fase 1).
 *
 * Patrón: Similar a carrefour-checkout/src/i18n/translate.ts
 *
 * Soporta:
 * - Notación de punto: `translate('paymentsUI.header.title')`
 * - Interpolación: `translate('paymentsUI.card.label', { last4: '1234' })`
 * - Locale switching: `setPaymentsUILocale('en')`
 * - Fallback estable: Si la clave no existe, devuelve la clave (no error)
 */

import { es } from './es';

type TranslationValue = string | { [key: string]: TranslationValue };
type TranslationTree = { [key: string]: TranslationValue };

const translations: Record<string, TranslationTree> = {};
let currentLocale = 'es';

/**
 * Registra un nuevo locale con su árbol de traducciones.
 * Util para agregar en' o cualquier otro idioma en el futuro.
 */
export function registerPaymentsUITranslations(
  locale: string,
  tree: TranslationTree,
): void {
  translations[locale] = tree;
}

/**
 * Cambia el locale activo para todas las traducciones de payments-ui.
 * Si el locale no está registrado, log warning y fallback a 'es'.
 */
export function setPaymentsUILocale(locale: string): void {
  if (!translations[locale]) {
    console.warn(
      `[payments-ui i18n] Locale '${locale}' not registered. Falling back to 'es'.`,
    );
  }
  currentLocale = locale;
}

// Registra el idioma español por defecto al importar el módulo
registerPaymentsUITranslations('es', es as TranslationTree);

/**
 * Interpola variables en un template.
 * Reemplaza {{key}} con el valor del objeto params.
 *
 * @example
 * interpolate('Hello {{name}}!', { name: 'John' }) // 'Hello John!'
 * interpolate('Last 4: {{last4}}', { last4: '1234' }) // 'Last 4: 1234'
 */
function interpolate(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, name: string) =>
    params[name] !== undefined ? String(params[name]) : `{{${name}}}`,
  );
}

/**
 * Traduce una clave con notación de punto.
 * Devuelve la clave como fallback si no existe (stable para UI).
 *
 * @example
 * translate('paymentsUI.header.title') // 'Selecciona método de pago'
 * translate('paymentsUI.card.label', { last4: '1234' }) // '**** 1234'
 * translate('paymentsUI.unknown.key') // 'paymentsUI.unknown.key' (fallback)
 */
export function translate(
  key: string,
  params?: Record<string, string | number>,
): string {
  // Fallback: si no tenemos traducciones para el locale, usa 'es'
  const tree = translations[currentLocale] ?? translations.es;
  if (!tree) {
    console.warn(
      `[payments-ui i18n] No translations loaded. Key: '${key}'. Returning key as fallback.`,
    );
    return key;
  }

  // Navega el árbol con notación de punto
  let value: TranslationValue | undefined = tree;
  for (const part of key.split('.')) {
    if (value && typeof value === 'object') {
      value = value[part];
    } else {
      value = undefined;
      break;
    }
  }

  // Si encontramos un string, interpola y devuelve
  // Si no, devuelve la clave como fallback
  return typeof value === 'string' ? interpolate(value, params) : key;
}
